import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import * as HttpStatus from 'http-status-codes';
import { debounce } from 'lodash';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { serialize } from '../../utils/helpers/model.helper';
import { WindowRefService } from 'i2g-ng-utils';
import { DocumentTypesEnum } from '../document-data/document-data.data';
import { Document } from '../document-data/document.model';
import { PaymentsSettings } from '../settings-data/sub-models/company-settings/payments.model';
import { DocumentPresetSettings } from '../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { GlobalBannerService } from '../shared/components/global-banner/global-banner.service';
import { IIcon } from '../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../shared/components/icon/icons-sizes.enum';
import { ERRORS } from '../shared/constants/errors.constant';
import { LoggerService } from '../shared/services/logger/logger.service';
import { Statement } from '../statement-data/models/statement.model';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../tracking/tracking.data';
import { RendererBuilder } from './renderer.builder';
import { RendererService } from './renderer.service';
import {
  distinctUntilChanged,
  filter,
  finalize,
  shareReplay,
  takeUntil,
} from 'rxjs/operators';
import { IDocumentDataDispatcher } from '../document-data/document-data-dispatcher.interface';
import { Toggles } from '../features-data/sub-models/toggles.model';
import { IPdfInfo } from './renderer.interface';

@Component({
  selector: 'app-renderer',
  templateUrl: 'renderer.component.html',
  styleUrls: ['renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [RendererBuilder],
})
export class RendererComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() documentDataDispatcher: IDocumentDataDispatcher;
  @Input() enableBankTransfer: boolean;
  @Input() enableCardPayments: boolean;
  @Input() enablePaypal: boolean;
  @Input() paymentsSettings: PaymentsSettings;
  @Input() renderableEntity: Document | Statement;
  @Input() viewableAsPdf: boolean;
  @Input() trackingObject: ITrackingObject;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() featureToggles: Toggles;

  @Output() onReRenderPdf: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('htmlPreview', { static: true }) htmlPreviewElement: ElementRef;

  private _htmlPreviewElement: any;
  private _urlCreator: any;
  private _loading: BehaviorSubject<boolean>;
  private _destroyed: Subject<boolean> = new Subject<boolean>();

  viewableAsHtml = true;
  pdfUrl: string;
  pdfSize: number;
  documentNumber: string;
  pdfNotFoundIcon: IIcon;
  loadingSpinnerIcon: IIcon;
  viewAsPdfFormControl: FormControl;
  documentType: DocumentTypesEnum;
  generateNewPdfTrackingAction: ITrackingAction;
  loading$: Observable<boolean>;
  pdfRenderingFailed$: BehaviorSubject<boolean>;

  constructor(
    private _loggerService: LoggerService,
    private _rendererBuilder: RendererBuilder,
    private _windowRefService: WindowRefService,
    private _rendererService: RendererService,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _globalBannerService: GlobalBannerService,
    private _renderer: Renderer2
  ) {}

  ngOnChanges(changes) {
    if (!this.viewAsPdfFormControl) {
      this.viewAsPdfFormControl = this._rendererBuilder.buildViewAsPdfFormControl(
        this.viewableAsHtml
      );
    }

    if (changes.renderableEntity || changes.paymentsSettings) {
      if (this.renderableEntity && this.renderableEntity instanceof Document) {
        this.documentType = this.renderableEntity.documentType;
        this.documentNumber = this.renderableEntity.docNumber;
      }

      this.pdfUrl = null;

      if (this.renderableEntity) {
        if (this.renderableEntity instanceof Document) {
          const document = this.renderableEntity as Document;

          this.viewableAsHtml = true;

          if (document.clientSignature) {
            this.viewableAsHtml =
              this.viewableAsHtml &&
              !document.clientSignature.signedPdfLegacySupport;
          }

          if (document.companySignature) {
            this.viewableAsHtml =
              this.viewableAsHtml &&
              !document.companySignature.signedPdfLegacySupport;
          }
        }

        if (this.viewAsPdfFormControl) {
          if (!this.viewableAsHtml) {
            this.viewAsPdfFormControl.reset(
              { value: true, disabled: true },
              { emitEvent: false }
            );
          } else {
            this.viewAsPdfFormControl.enable({ emitEvent: false });
          }
        }

        this.renderRenderableEntityDebounced(
          this.renderableEntity,
          !this.viewableAsHtml || this.viewAsPdfFormControl.value,
          this.paymentsSettings
        );
      }
    }
  }

  ngOnInit() {
    const win = this._windowRefService.nativeWindow;

    this._urlCreator = win.URL || win.webkitURL;
    this._loading = new BehaviorSubject<boolean>(true);

    this.pdfNotFoundIcon = {
      name: 'pdf-not-found',
      size: IconSizesEnum.custom,
    };
    this.loadingSpinnerIcon = {
      name: 'loading-spinner',
      size: IconSizesEnum.large,
    };
    this.generateNewPdfTrackingAction = this._trackingActionBuilder.buildButtonTapped(
      'generate_invoice',
      this.trackingScreenName
    );
    this.pdfRenderingFailed$ = new BehaviorSubject<boolean>(false);

    if (!this.renderableEntity) {
      this.viewAsPdfFormControl.disable();
    }

    this.viewAsPdfFormControl.valueChanges
      .pipe(
        takeUntil(this._destroyed),
        filter(() => this.viewableAsPdf)
      )
      .subscribe((viewAsPdf) => {
        this._loading.next(true);

        this.renderRenderableEntityDebounced(
          this.renderableEntity,
          viewAsPdf,
          this.paymentsSettings
        );
      });

    this.loading$ = this._loading
      .asObservable()
      .pipe(distinctUntilChanged(), takeUntil(this._destroyed), shareReplay(1));
  }

  ngAfterViewInit() {
    this._htmlPreviewElement = this.htmlPreviewElement.nativeElement;

    this.scaleRenderedContent();

    if (this.renderableEntity) {
      this.renderRenderableEntityDebounced(
        this.renderableEntity,
        !this.viewableAsHtml || this.viewAsPdfFormControl.value,
        this.paymentsSettings
      );
    }
  }

  renderRenderableEntity(
    renderableEntity: Document | Statement,
    asPdf: boolean,
    payments: PaymentsSettings
  ) {
    if (renderableEntity instanceof Document) {
      this._renderRenderableDocument(
        renderableEntity as Document,
        asPdf,
        payments
      );
    } else {
      this._renderRenderableStatement(renderableEntity as Statement, asPdf);
    }
  }

  renderRenderableEntityDebounced = debounce(
    this.renderRenderableEntity.bind(this),
    150
  );

  @HostListener('window:resize', ['$event'])
  onWindowResize($event) {
    this.scaleRenderedContentDebounced();
  }

  scaleRenderedContent() {
    if (this._htmlPreviewElement) {
      const contentElement = this._htmlPreviewElement;
      const contentWidth = contentElement.offsetWidth;
      const containerElement = contentElement.parentNode;
      const containerWidth = containerElement.offsetWidth - 15;

      this._renderer.setStyle(
        contentElement,
        'transform',
        contentWidth > containerWidth
          ? `scale(${containerWidth / contentWidth})`
          : 'none'
      );
    }
  }

  scaleRenderedContentDebounced = debounce(
    this.scaleRenderedContent.bind(this),
    250
  );

  reRenderPdf() {
    this._loading.next(true);

    this.pdfRenderingFailed$.next(false);

    this.onReRenderPdf.emit();
  }

  private _renderRenderableDocument(
    document: Document,
    asPdf: boolean,
    payments: PaymentsSettings
  ) {
    const renderableDocument = serialize(document).toJS();

    let bankTransferEnabled: boolean;
    let cardPaymentsEnabled: boolean;
    let paypalEcEnabled: boolean;

    if (payments) {
      const documentSettings: DocumentPresetSettings = this.renderableEntity
        .settings;

      bankTransferEnabled =
        this.enableBankTransfer &&
        payments.bankTransferEnabled() &&
        !documentSettings.bankTransferDisabled;
      cardPaymentsEnabled =
        this.enableCardPayments &&
        payments.cardPaymentsEnabled() &&
        !documentSettings.cardPaymentsDisabled;
      paypalEcEnabled =
        this.enablePaypal &&
        payments.paypalEcEnabled &&
        !documentSettings.paypalEcDisabled;
    }

    if (asPdf && !this.pdfUrl && this.viewableAsPdf) {
      this.pdfRenderingFailed$.next(false);

      this._rendererService
        .renderDocumentAsPdf(
          renderableDocument,
          bankTransferEnabled,
          cardPaymentsEnabled,
          paypalEcEnabled
        )
        .pipe(
          finalize(() => this._loading.next(false)),
          takeUntil(this._destroyed)
        )
        .subscribe(
          (body: IPdfInfo) => {
            this.pdfUrl = body.url;
            this.pdfSize = body.size;
          },
          (error) => this._onRenderPdfError(error)
        );
    } else {
      this._loggerService.log('rendering document:', renderableDocument);

      if (this._htmlPreviewElement.style.transform === 'none') {
        this._renderer.setStyle(
          this._htmlPreviewElement,
          'visibility',
          'hidden'
        );
      }

      this._rendererService
        .renderDocument(
          this._htmlPreviewElement,
          renderableDocument,
          bankTransferEnabled,
          cardPaymentsEnabled,
          paypalEcEnabled
        )
        .then(() => this._onRenderSuccess())
        .catch((error) => this._onRenderError(error));
    }
  }

  private _renderRenderableStatement(statement: Statement, asPdf: boolean) {
    const renderableEntity = serialize(statement).toJS();
    const cardPaymentsEnabled = statement.content.cardPaymentsEnabled;
    const paypalEcEnabled = statement.content.paypalEcEnabled;

    if (asPdf && !this.pdfUrl && this.viewableAsPdf) {
      this.pdfRenderingFailed$.next(false);

      this._rendererService
        .renderStatementAsPdf(
          renderableEntity,
          cardPaymentsEnabled,
          paypalEcEnabled
        )
        .pipe(
          finalize(() => this._loading.next(false)),
          takeUntil(this._destroyed)
        )
        .subscribe(
          (body: IPdfInfo) => {
            this.pdfUrl = body.url;
            this.pdfSize = body.size;
          },
          (error) => this._onRenderPdfError(error)
        );
    } else {
      this._loggerService.log('rendering statement:', renderableEntity);

      if (this._htmlPreviewElement.style.transform === 'none') {
        this._renderer.setStyle(
          this._htmlPreviewElement,
          'visibility',
          'hidden'
        );
      }

      this._rendererService
        .renderStatement(
          this._htmlPreviewElement,
          renderableEntity,
          cardPaymentsEnabled,
          paypalEcEnabled
        )
        .then(() => this._onRenderSuccess())
        .catch((error) => this._onRenderError(error));
    }
  }

  private _onRenderSuccess() {
    this._loading.next(false);

    setTimeout(() => {
      this.scaleRenderedContent();

      this._renderer.setStyle(
        this._htmlPreviewElement,
        'visibility',
        'inherit'
      );
    });
  }

  private _onRenderError(error) {
    this._globalBannerService.showSystemErrorBanner(error);

    this._renderer.setStyle(this._htmlPreviewElement, 'visibility', 'inherit');
  }

  private _onRenderPdfError([status, errorBody, error]: any[]) {
    if (
      status === HttpStatus.NOT_FOUND &&
      errorBody &&
      errorBody.code === ERRORS.PDF_RENDERING_FAILED
    ) {
      this.pdfRenderingFailed$.next(true);
    } else {
      this._globalBannerService.showSystemErrorBanner(error);
    }
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
